import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/store';
import { cn } from '@/lib/utils';
import {
  X, Upload, FileText, Trash2, RefreshCw, Save,
  Plus, Download, Loader2, ChevronRight,
} from 'lucide-react';
import {
  listTemplates,
  uploadTemplate,
  patchTemplate,
  reanalyzeTemplate,
  deleteTemplate,
  type TemplateMeta,
  type TemplateField,
} from '@/services/api';

const FIELD_TYPES = ['text', 'phone', 'address', 'date', 'amount', 'email', 'number'];

export function AdminPanel() {
  const adminOpen = useAppStore((s) => s.adminOpen);
  const setAdminOpen = useAppStore((s) => s.setAdminOpen);

  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listTemplates();
      setTemplates(list);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminOpen) refresh();
  }, [adminOpen]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const meta = await uploadTemplate(file);
      await refresh();
      setSelectedId(meta.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Видалити шаблон? Дію неможливо скасувати.')) return;
    try {
      await deleteTemplate(id);
      if (selectedId === id) setSelectedId(null);
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const selected = templates.find((t) => t.id === selectedId) || null;

  if (!adminOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4">
      <div className="w-full max-w-6xl h-full sm:h-[88vh] bg-[#0c0c0e] border-0 sm:border sm:border-white/10 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b border-white/10 px-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-blue-400" />
            <h2 className="text-sm font-semibold text-white">Адмін: еталонні шаблони документів</h2>
            <span className="text-xs text-white/40">({templates.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
              title="Оновити"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </button>
            <button
              onClick={() => setAdminOpen(false)}
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        {error && (
          <div className="px-5 py-2 bg-red-500/10 border-b border-red-500/30 text-xs text-red-300">
            {error}
          </div>
        )}

        <div className="flex-1 flex flex-col xl:flex-row min-h-0">
          {/* List */}
          <div className={cn(
            'w-full xl:w-72 border-b xl:border-b-0 xl:border-r border-white/10 flex flex-col min-h-0',
            // On mobile: shrink list when a template is selected so the editor has room
            selected ? 'h-[40vh] xl:h-auto' : 'flex-1 xl:flex-none'
          )}>
            <div className="p-3 border-b border-white/5">
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Аналізую…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" /> Завантажити .docx
                  </>
                )}
              </button>
              <p className="text-[10px] text-white/30 text-center mt-2">
                Завантажте еталонний договір. LLM розбере тип документа і поля.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {!loading && templates.length === 0 && (
                <div className="text-center py-10 px-4 text-white/30 text-xs">
                  Ще немає шаблонів.<br />Завантажте перший .docx документ.
                </div>
              )}
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex items-start gap-2 group',
                    selectedId === t.id && 'bg-white/10'
                  )}
                >
                  <FileText className="h-4 w-4 text-white/40 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {t.doc_type || t.documentType || 'Без назви'}
                    </div>
                    <div className="text-[11px] text-white/40 truncate">
                      {t.originalName || t.filename}
                    </div>
                    <div className="text-[10px] text-white/30 mt-0.5">
                      {(t.fields || t.requiredFields || []).length} полів
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-white/20 shrink-0 mt-1 group-hover:text-white/50" />
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
            {!selected && (
              <div className="h-full flex items-center justify-center text-white/30 text-sm">
                Виберіть шаблон зі списку
              </div>
            )}
            {selected && (
              <TemplateEditor
                key={selected.id}
                template={selected}
                onSaved={refresh}
                onDelete={() => handleDelete(selected.id)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({
  template,
  onSaved,
  onDelete,
}: {
  template: TemplateMeta;
  onSaved: () => void;
  onDelete: () => void;
}) {
  const [docType, setDocType] = useState(template.doc_type || template.documentType || '');
  const [description, setDescription] = useState(template.description || '');
  const [fields, setFields] = useState<TemplateField[]>(
    template.fields || template.requiredFields || []
  );
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await patchTemplate(template.id, { docType, description, fields });
      setSavedAt(Date.now());
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReanalyze = async () => {
    if (!confirm('Запустити повторний LLM-аналіз шаблону? Це перепише поточні поля.')) return;
    setReanalyzing(true);
    setError(null);
    try {
      const updated = await reanalyzeTemplate(template.id);
      setDocType(updated.doc_type || updated.documentType || '');
      setDescription(updated.description || '');
      setFields(updated.fields || updated.requiredFields || []);
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setReanalyzing(false);
    }
  };

  const updateField = (idx: number, patch: Partial<TemplateField>) => {
    setFields((arr) => arr.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  const addField = () => {
    setFields((arr) => [...arr, { key: '', label: '', type: 'text', description: '' }]);
  };

  const removeField = (idx: number) => {
    setFields((arr) => arr.filter((_, i) => i !== idx));
  };

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/30 mb-1">Файл</div>
          <div className="text-sm text-white/80 truncate">{template.originalName || template.filename}</div>
          <div className="text-[11px] text-white/30 mt-0.5">id: {template.id}</div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <a
            href={`/api/templates/${template.id}/download`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/80 transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Завантажити
          </a>
          <button
            onClick={handleReanalyze}
            disabled={reanalyzing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/80 transition-colors disabled:opacity-50"
          >
            {reanalyzing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Переаналізувати
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-xs text-red-300 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" /> Видалити
          </button>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300">
          {error}
        </div>
      )}

      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/30 mb-1.5 block">
          Тип документа
        </label>
        <input
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          placeholder="напр. Позовна заява, Договір оренди…"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-blue-600/50"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/30 mb-1.5 block">
          Опис
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Короткий опис документа і коли він використовується"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-blue-600/50 resize-y min-h-[60px]"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-widest text-white/30">
            Реквізити / поля ({fields.length})
          </label>
          <button
            onClick={addField}
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> додати
          </button>
        </div>

        {fields.length === 0 && (
          <div className="text-center py-6 text-white/30 text-xs border border-dashed border-white/10 rounded-lg">
            Полів не знайдено. Натисніть «Переаналізувати» або додайте вручну.
          </div>
        )}

        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/10 rounded-lg p-3 space-y-2">
              <div className="flex flex-wrap sm:flex-nowrap gap-2 items-start">
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] uppercase text-white/30">Ключ</label>
                  <input
                    value={f.key}
                    onChange={(e) => updateField(i, { key: e.target.value })}
                    placeholder="plaintiff_name"
                    className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white font-mono focus:outline-none focus:border-blue-600/50"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] uppercase text-white/30">Назва</label>
                  <input
                    value={f.label}
                    onChange={(e) => updateField(i, { label: e.target.value })}
                    placeholder="ПІБ позивача"
                    className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-600/50"
                  />
                </div>
                <div className="w-28 shrink-0">
                  <label className="text-[10px] uppercase text-white/30">Тип</label>
                  <select
                    value={f.type || 'text'}
                    onChange={(e) => updateField(i, { type: e.target.value })}
                    className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-600/50"
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => removeField(i)}
                  className="self-end p-1.5 text-white/30 hover:text-red-400 transition-colors"
                  title="Видалити поле"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <input
                value={f.description || ''}
                onChange={(e) => updateField(i, { description: e.target.value })}
                placeholder="Опис поля (необовʼязково)"
                className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white/80 focus:outline-none focus:border-blue-600/50"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10">
        <div className="text-[11px] text-white/30">
          {savedAt && Date.now() - savedAt < 4000 && (
            <span className="text-emerald-400">✓ Збережено</span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Зберегти
        </button>
      </div>
    </div>
  );
}
